import { useAuth } from "@/contexts/AuthContext";
import { JobCard } from "@/components/JobCard";
import { TeaserJobCard } from "@/components/TeaserJobCard";
import { SignUpGate } from "@/components/SignUpGate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Users, TrendingUp, Zap, ArrowRight } from "lucide-react";

interface SmartJobDisplayProps {
  jobs: any[];
  isSearch?: boolean;
  maxFreeViews?: number;
  showSignUpPrompt?: boolean;
}

export const SmartJobDisplay = ({ 
  jobs, 
  isSearch = false, 
  maxFreeViews = 3,
  showSignUpPrompt = true 
}: SmartJobDisplayProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // For authenticated users, show all jobs normally
  if (user) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {jobs.map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>
    );
  }

  // For unauthenticated users, implement teaser strategy
  const freeJobs = jobs.slice(0, maxFreeViews);
  const lockedJobs = jobs.slice(maxFreeViews);
  const totalHiddenJobs = Math.max(jobs.length - maxFreeViews, 0);

  return (
    <div className="space-y-8">
      {/* Free preview jobs */}
      {freeJobs.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {freeJobs.map((job, index) => (
            <div key={job.id} className="relative">
              <JobCard job={job} />
              {index === freeJobs.length - 1 && (
                <Badge className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white">
                  Free Preview
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Teaser jobs (partially visible) */}
      {lockedJobs.length > 0 && (
        <div className="space-y-6">
          {/* Sign-up prompt card */}
          {showSignUpPrompt && (
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-primary rounded-full flex items-center justify-center">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      {totalHiddenJobs}+ More Jobs Available
                    </h3>
                    <p className="text-muted-foreground">
                      Sign up to view all {jobs.length} matching positions, including salary details, company info, and apply instantly.
                    </p>
                  </div>

                  <div className="flex justify-center items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span>10k+ Active Jobs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span>500+ Companies</span>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/auth?step=signup')} 
                    className="bg-gradient-primary hover:opacity-90 transition-opacity"
                    size="lg"
                  >
                    Sign Up Free - See All Jobs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  
                  <p className="text-xs text-muted-foreground">
                    Free forever • No credit card required • Takes 30 seconds
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Teaser job cards */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {lockedJobs.slice(0, 6).map((job, index) => (
              <TeaserJobCard 
                key={job.id} 
                job={job} 
                showPartial={index < 2} // Show partial info for first 2, then fully blur
              />
            ))}
          </div>

          {/* Additional locked jobs indicator */}
          {lockedJobs.length > 6 && (
            <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
              <CardContent className="p-8 text-center">
                <div className="text-lg font-semibold text-primary mb-2">
                  + {lockedJobs.length - 6} More Premium Jobs
                </div>
                <p className="text-muted-foreground mb-4">
                  Including exclusive positions from top employers
                </p>
                <Button 
                  onClick={() => navigate('/auth?step=signup')} 
                  variant="outline" 
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                >
                  Unlock All Jobs
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};